import { Injectable } from '@angular/core';
import { defer, firstValueFrom, from, map, Observable } from 'rxjs';
import {
  LibraryCursor,
  LibraryItem,
  LibraryPage,
  LibraryService,
} from 'application/src/app/services/library.service';

// What the user was looking at when playback started — the iOS PlaylistManager's
// playingTagId/section, reduced to what the next-item rules actually need.
export interface PlayQueueContext {
  // The tag's ordered itemIds when playback started from a tag-filtered library; the tag
  // order IS the playlist order, and a tag playlist does not wrap around.
  tagItemIds: string[] | null;
  // True when playback started from the Completed tab: the user is deliberately replaying
  // finished items, so fully-read ones stay eligible.
  includeCompleted: boolean;
}

const DEFAULT_CONTEXT: PlayQueueContext = { tagItemIds: null, includeCompleted: false };

// Picks the item autoplay advances to when one finishes — a port of iOS
// PlaylistManager.getNextItemToPlay. Deliberately stateless beyond the context: the
// library is re-fetched at item end, so the ordering is as fresh as iOS's live data source.
@Injectable({ providedIn: 'root' })
export class PlayQueueService {
  private context: PlayQueueContext = DEFAULT_CONTEXT;

  // Announced by the library just before it starts playback; adopted (or defaulted) by the
  // player when a NEW item actually starts. The handshake keeps a stale context from
  // leaking into playback that starts elsewhere — e.g. the reader's Play after an earlier
  // Completed-tab session would otherwise keep includeCompleted forever.
  private pendingContext: PlayQueueContext | null = null;

  constructor(private library: LibraryService) {}

  // Called by the library whenever playback starts from a row.
  setContext(context: PlayQueueContext): void {
    this.pendingContext = context;
  }

  // Called by PlayerService when a user-picked item starts (or an ended item is replayed):
  // adopts the announced context, or resets to the default when playback started outside
  // a library row.
  consumePendingContext(): void {
    this.context = this.pendingContext ?? DEFAULT_CONTEXT;
    this.pendingContext = null;
  }

  // A library-row toggle that merely paused/resumed the current item never reaches
  // playItem — drop its announcement so it can't be adopted by a later unrelated start.
  discardPendingContext(): void {
    this.pendingContext = null;
  }

  // Back to the default scope entirely — a sign-out/account switch must not carry one
  // account's Completed/tag scope into the next session (a restored paused item starts
  // playing without ever passing through playItem's consume).
  resetContext(): void {
    this.context = DEFAULT_CONTEXT;
    this.pendingContext = null;
  }

  findNext(
    userId: string,
    currentItemId: string,
    playedItemIds: ReadonlySet<string>
  ): Observable<LibraryItem | null> {
    const currentId = currentItemId.toLowerCase();
    const tagItemIds = this.context.tagItemIds;
    const isTagScoped = !!tagItemIds?.some(id => id.toLowerCase() === currentId);

    if (isTagScoped) {
      // The tag's own order is the playlist; small by construction, fetched whole.
      // iOS: a tag playlist never skips fully-read items and never wraps around.
      return this.library.getItemsByIds(userId, tagItemIds!).pipe(
        map(items => {
          const start = itemIndexIn(items, currentId) + 1;

          return (
            items.slice(start).find(item => isEligible(item, currentId, playedItemIds, false)) ??
            null
          );
        })
      );
    }

    // iOS skips fully-read items in the library-wide list unless playback started from
    // the Completed section.
    const skipFullyRead = !this.context.includeCompleted;

    return defer(() =>
      from(this.findNextInLibrary(userId, currentId, playedItemIds, skipFullyRead))
    );
  }

  // Walks the library page by page (updatedAt desc, the tab order) and stops at the FIRST
  // eligible item after the current one — the successor is usually on page one, and
  // loading the whole library at every playback boundary would cost a read per item.
  // Wrap-around uses the items accumulated on the way to the end, so no page is fetched
  // twice; updatedAt deliberately doesn't move on progress writes, so the walk is stable.
  private async findNextInLibrary(
    userId: string,
    currentId: string,
    playedItemIds: ReadonlySet<string>,
    skipFullyRead: boolean
  ): Promise<LibraryItem | null> {
    const beforeCurrent: LibraryItem[] = [];
    let seenCurrent = false;
    let cursor: LibraryCursor | null = null;

    do {
      // Annotated: the cursor feeds back into the next getItems call, and TS refuses the
      // resulting circular inference without it.
      const page: LibraryPage = await firstValueFrom(this.library.getItems(userId, 'all', cursor));

      for (const item of page.items) {
        if (item.id.toLowerCase() === currentId) {
          seenCurrent = true;
          continue;
        }

        if (seenCurrent) {
          if (isEligible(item, currentId, playedItemIds, skipFullyRead)) {
            return item;
          }
        } else {
          beforeCurrent.push(item);
        }
      }

      cursor = page.cursor;
    } while (cursor);

    // Nothing after the current item — wrap around to what preceded it. When the current
    // item has left the list entirely (deleted mid-listen), beforeCurrent holds the whole
    // library and this degrades to a scan from the top.
    return (
      beforeCurrent.find(item => isEligible(item, currentId, playedItemIds, skipFullyRead)) ?? null
    );
  }
}

function itemIndexIn(items: LibraryItem[], id: string): number {
  return items.findIndex(item => item.id.toLowerCase() === id);
}

function isEligible(
  item: LibraryItem,
  currentId: string,
  playedItemIds: ReadonlySet<string>,
  skipFullyRead: boolean
): boolean {
  const id = item.id.toLowerCase();

  if (id === currentId || playedItemIds.has(id)) {
    return false;
  }

  // Only successfully processed items can stream.
  if (item.status !== 'success') {
    return false;
  }

  if (skipFullyRead && (item.isFullyRead || item.libraryState === 'completed')) {
    return false;
  }

  return true;
}
